import { timeout } from '@art-suite/art-core-ts';
import { getScanPhotoQueryKey } from '@kaiizen/kai-pro-api';
import { errorIsMissing } from '@kaiizen/zod-less-api';
import PQueue from 'p-queue';

import { BestPhotoSizeResult, BestPhotoSizeScanDirection, PhotoSize } from '../contexts/syncing/Photos/PhotoGalleryContext';
import { isHardFailure } from '../contexts/syncing/ScanSynchronizer/syncLib';
import { FileCacheInterface } from '../FileSystem/FileCacheInterface';
import { ConnectionInfo, FileSystemInterface } from '../FileSystem/FileSystemInterface';
import { isBrowser } from '../isBrowser';
import { type RealtimeClient } from '../realtime';

const ORIGINAL_SIZE = 'original';

const getInflightKey = (photoId: string, size: PhotoSize): string => `${photoId}::${size}`;

/**
 * Cache file name convention. Originals are `<photoId>.jpg`; scaled photos are
 * `<photoId>_<size>.jpg`. The bare file name is the FileCache key.
 */
export const getCacheFileName = (photoId: string, size: PhotoSize): string => size === ORIGINAL_SIZE ? `${photoId}.jpg` : `${photoId}_${size}.jpg`;

const PHOTO_SEQUENCE_BY_DIRECTION: Record<BestPhotoSizeScanDirection, Record<PhotoSize, PhotoSize[]>> = {
  lowerResolution: {
    256: [256],
    512: [512, 256],
    1024: [1024, 512, 256],
    2048: [2048, 1024, 512, 256],
    original: [ORIGINAL_SIZE, 2048, 1024, 512, 256],
  },
  higherResolution: {
    256: [256, 512, 1024, 2048, ORIGINAL_SIZE],
    512: [512, 1024, 2048, ORIGINAL_SIZE],
    1024: [1024, 2048, ORIGINAL_SIZE],
    2048: [2048, ORIGINAL_SIZE],
    original: [ORIGINAL_SIZE],
  },
  both: {
    256: [256, 512, 1024, 2048, ORIGINAL_SIZE],
    512: [512, 256, 1024, 2048, ORIGINAL_SIZE],
    1024: [1024, 512, 2048, ORIGINAL_SIZE, 256],
    2048: [2048, ORIGINAL_SIZE, 1024, 512, 256],
    original: [ORIGINAL_SIZE, 2048, 1024, 512, 256],
  },
};

const getPhotoSequenceByDirection = (requestedSize: PhotoSize, scanDirection?: BestPhotoSizeScanDirection): PhotoSize[] => {
  const sequenceForDirection = PHOTO_SEQUENCE_BY_DIRECTION[scanDirection ?? 'lowerResolution'] ?? PHOTO_SEQUENCE_BY_DIRECTION.lowerResolution;
  return sequenceForDirection[requestedSize] ?? sequenceForDirection[1024];
};

const DOWNLOAD_RETRY_LIMIT = 3;
const DOWNLOAD_RETRY_BASE_DELAY_MS = 50;

/**
 * A 404 on a photo the client itself just created (offline-sync upload) usually means the
 * upload landed a moment before the server finished writing the DB row — this can take
 * anywhere from under a second to a couple of minutes into a large in-progress batch
 * upload. Wait for a realtime push telling us the photo is now available (see
 * waitForPhotoAvailable) — no blind polling; only an actual push re-triggers an attempt —
 * bounded by this max wait so a genuinely deleted/never-uploaded photo doesn't wait forever.
 * Overridable via GalleryPhotoLoaderOptions, primarily so tests can use small values.
 */
const DEFAULT_MISSING_PHOTO_MAX_WAIT_MS = 2 * 60 * 1000;

/**
 * Max concurrent photo downloads. Browsers throttle outgoing fetches per origin
 * but we still want to cap how many we kick off so gallery scrolling doesn't
 * starve unrelated app traffic. Native (RN) is more constrained; keep it lower.
 */
const BROWSER_DOWNLOAD_CONCURRENCY = 20;
const NATIVE_DOWNLOAD_CONCURRENCY = 10;
const downloadQueue = new PQueue({ concurrency: isBrowser ? BROWSER_DOWNLOAD_CONCURRENCY : NATIVE_DOWNLOAD_CONCURRENCY });

export interface GalleryPhotoLoaderOptions {
  fileSystem: FileSystemInterface;
  fileCache: FileCacheInterface;
  /** Directory used as a write-staging area for downloads and scales. Files land here and are then moved into the FileCache via addFile. With trailing slash. */
  stagingDir: string;
  /** Ensures the staging directory exists. Awaited before the first staging write. */
  ensureStagingDirExists: () => Promise<void>;
  /** Returns the absolute path to a photo's unsynced (doc-dir) location, or undefined if not unsynced. */
  getUnsyncedPhotoPath: (photoId: string) => Promise<string | undefined>;
  getConnectionInfo: () => ConnectionInfo;
  /** Optional realtime client used to wait for a push notification when a photo 404s. Without one, a 404 fails immediately — there's nothing to wait on. */
  realtimeClient?: RealtimeClient;
  /** Override for the missing-photo max wait (see DEFAULT_MISSING_PHOTO_MAX_WAIT_MS). Primarily for fast tests. */
  missingPhotoMaxWaitMs?: number;
}

/**
 * Loads photos for the gallery — decides whether the requested size is already
 * locally available (and where), and otherwise downloads or scales it into the
 * FileCache. The single owner of:
 *
 *   - "do we have an appropriately-sized photo available?" (sync + async paths)
 *   - "should we be fetching the photo?" (download vs. scale-from-larger)
 *   - "make sure we only fetch it once even if multiple asks" (inflight dedup)
 *   - retry / backoff
 *
 * Public surface:
 *   getPhoto(photoId, size)                          — exact size; downloads/scales as needed
 *   getBestLocallyAvailablePhoto(photoId, size, dir?) — best already-on-disk fallback; never downloads
 *   getBestLocallyAvailablePhotoSync(photoId, size)   — sync variant; serves from FileCache.getFileSync
 *
 * No in-memory mirror — sync access is served by FileCache's own hot mirror
 * via {@link FileCacheInterface.getFileSync}. Cold entries on persistent-store
 * backends return undefined sync; caller falls through to the async variant.
 */
export class GalleryPhotoLoader {
  private readonly fileSystem: FileSystemInterface;
  private readonly fileCache: FileCacheInterface;
  private readonly stagingDir: string;
  private readonly ensureStagingDirExists: () => Promise<void>;
  private readonly getUnsyncedPhotoPath: (photoId: string) => Promise<string | undefined>;
  private readonly getConnectionInfo: () => ConnectionInfo;
  private readonly realtimeClient?: RealtimeClient;
  private readonly missingPhotoMaxWaitMs: number;

  /** Inflight getPhoto requests, keyed by `${photoId}::${size}`. Dedups concurrent callers. */
  private readonly inflightGetPhotos = new Map<string, Promise<string | undefined>>();

  constructor(options: GalleryPhotoLoaderOptions) {
    this.fileSystem = options.fileSystem;
    this.fileCache = options.fileCache;
    this.stagingDir = options.stagingDir;
    this.ensureStagingDirExists = options.ensureStagingDirExists;
    this.getUnsyncedPhotoPath = options.getUnsyncedPhotoPath;
    this.getConnectionInfo = options.getConnectionInfo;
    this.realtimeClient = options.realtimeClient;
    this.missingPhotoMaxWaitMs = options.missingPhotoMaxWaitMs ?? DEFAULT_MISSING_PHOTO_MAX_WAIT_MS;
  }

  // ********************************************
  // Public API
  // ********************************************

  /**
   * (Slowest) Get a URI to the requested photo and size, downloading or scaling if needed.
   * Dedups concurrent callers for the same photoId+size; retries with backoff on transient failures.
   */
  async getPhoto(photoId: string, requestedSize: PhotoSize): Promise<string | undefined> {
    const existing = await this.findExistingResult(photoId, requestedSize);
    if (existing) return existing.uri;

    const inflightKey = getInflightKey(photoId, requestedSize);
    const existingInflight = this.inflightGetPhotos.get(inflightKey);
    if (existingInflight) return existingInflight;

    const inflight = this._getPhoto(photoId, requestedSize);
    this.inflightGetPhotos.set(inflightKey, inflight);
    inflight.finally(() => this.inflightGetPhotos.delete(inflightKey)).catch(() => {});
    return inflight;
  }

  /**
   * (Fast) Get the best, locally available version of the photo per the
   * configured fallback sequence. Resolves with one bulk FileCache query and
   * does NOT update LRU order — gallery views and prefetch can probe freely.
   *
   * Unsynced originals live outside the FileCache and are checked separately:
   *   - sequence leader is ORIGINAL_SIZE → prefer unsynced over cache (it's the canonical source)
   *   - ORIGINAL_SIZE mid-sequence       → cache first, unsynced as last-resort fallback
   */
  async getBestLocallyAvailablePhoto(
    photoId: string,
    requestedSize: PhotoSize,
    scanDirection?: BestPhotoSizeScanDirection,
  ): Promise<BestPhotoSizeResult | undefined> {
    const sequence = getPhotoSequenceByDirection(requestedSize, scanDirection);

    if (sequence[0] === ORIGINAL_SIZE) {
      const unsyncedPath = await this.getUnsyncedOriginalIfPresent(photoId);
      if (unsyncedPath) return { uri: this.fileSystem.pathToUri(unsyncedPath), size: ORIGINAL_SIZE };
    }

    const cached = await this.firstCachedInSequence(photoId, sequence);
    if (cached) return { uri: this.fileSystem.pathToUri(cached.path), size: cached.size };

    if (sequence.includes(ORIGINAL_SIZE) && sequence[0] !== ORIGINAL_SIZE) {
      const unsyncedPath = await this.getUnsyncedOriginalIfPresent(photoId);
      if (unsyncedPath) return { uri: this.fileSystem.pathToUri(unsyncedPath), size: ORIGINAL_SIZE };
    }

    return undefined;
  }

  /**
   * (Fastest) Synchronous variant served directly from the FileCache's hot mirror.
   * Returns the first sequence entry whose file is sync-available; undefined when
   * nothing in the sequence is warm. Caller should fall through to the async
   * variant on undefined.
   *
   * Unsynced originals live outside the FileCache and are NOT discoverable here —
   * if you need to surface them synchronously, the async path will find them on
   * the next tick.
   */
  getBestLocallyAvailablePhotoSync(photoId: string, requestedSize: PhotoSize, scanDirection?: BestPhotoSizeScanDirection): BestPhotoSizeResult | undefined {
    for (const size of getPhotoSequenceByDirection(requestedSize, scanDirection)) {
      const path = this.fileCache.getFileSync(getCacheFileName(photoId, size));
      if (path) return { uri: this.fileSystem.pathToUri(path), size };
    }
    return undefined;
  }

  // ********************************************
  // Private — single-result resolution (used by getPhoto fast-path)
  // ********************************************

  private async findExistingResult(photoId: string, size: PhotoSize): Promise<BestPhotoSizeResult | undefined> {
    if (size === ORIGINAL_SIZE) {
      const unsyncedPath = await this.getUnsyncedOriginalIfPresent(photoId);
      if (unsyncedPath) return { uri: this.fileSystem.pathToUri(unsyncedPath), size: ORIGINAL_SIZE };
    }
    const path = await this.fileCache.getFile(getCacheFileName(photoId, size));
    if (!path) return undefined;
    return { uri: this.fileSystem.pathToUri(path), size };
  }

  /**
   * Single attempt: scale from a smaller cached size if available, otherwise download,
   * then hand the result off to the FileCache via addFile. Throws on any failure.
   */
  private async attemptDownloadOrScale(photoId: string, requestedSize: PhotoSize, stagingPath: string): Promise<string> {
    // Prefer scaling from the smallest cached size that is ≥ requestedSize — cheaper than network.
    const localScaleSourcePath = requestedSize !== ORIGINAL_SIZE
      ? await this.findSmallestCachedAtLeast(photoId, requestedSize)
      : null;
    const scaling = localScaleSourcePath !== null && requestedSize !== ORIGINAL_SIZE;
    if (scaling) {
      await this.fileSystem.scaleImageFile(localScaleSourcePath, stagingPath, requestedSize as number);
    } else {
      await downloadQueue.add(() => this.fileSystem.downloadPhoto(photoId, stagingPath, requestedSize, this.getConnectionInfo()));
    }
    const cachedPath = await this.fileCache.addFile(stagingPath);
    return this.fileSystem.pathToUri(cachedPath);
  }

  /**
   * A 404 on a brand-new photo usually means the client asked before the server finished
   * writing the DB row. Without a realtime client there's nothing
   * to wait on, so this fails immediately — same as before this whole wait mechanism existed.
   * With one, subscribe once and wait for an actual push telling us the photo is now
   * available; no blind polling — only a push re-triggers an attempt, bounded by
   * missingPhotoMaxWaitMs so a genuinely deleted/never-uploaded photo doesn't wait forever.
   * A transient (soft) failure after a push is tolerated the same as still-missing — we're
   * already committed to waiting this out; only a genuinely non-recoverable client error (a
   * hard failure that ISN'T just "not found yet") ends the wait early. Gives up (falls back
   * to any cached/unsynced original) once the max wait is exhausted.
   */
  private async waitForPhotoAvailable(photoId: string, requestedSize: PhotoSize, stagingPath: string): Promise<string | undefined> {
    if (!this.realtimeClient) return undefined;
    const realtimeClient = this.realtimeClient;

    // Declared before subscribe() so the callback (which may fire synchronously, during the
    // subscribe() call itself) always closes over an already-initialized variable — never a TDZ read.
    let onPush: (() => void) | undefined;
    let subscription: ReturnType<RealtimeClient['subscribe']> | undefined;
    try {
      subscription = realtimeClient.subscribe(getScanPhotoQueryKey(photoId), 'refetch', () => onPush?.());
    } catch (error) {
      // A broken realtime client leaves nothing to wait on — no polling fallback.
      console.log(`getPhoto: realtimeClient.subscribe failed while waiting for photo availability (photoId: ${photoId})`, error);
      return undefined;
    }

    const deadline = Date.now() + this.missingPhotoMaxWaitMs;
    try {
      while (true) {
        const remainingMs = deadline - Date.now();
        if (remainingMs <= 0) return await this.getFinalFallback(photoId);

        const pushed = await new Promise<boolean>(resolve => {
          const timer = setTimeout(() => {
            onPush = undefined;
            resolve(false);
          }, remainingMs);
          onPush = () => {
            clearTimeout(timer);
            resolve(true);
          };
        });
        if (!pushed) return await this.getFinalFallback(photoId); // max wait elapsed with no push

        try {
          return await this.attemptDownloadOrScale(photoId, requestedSize, stagingPath);
        } catch (error) {
          await this.cleanupStagingFile(stagingPath);
          if (!errorIsMissing(error) && isHardFailure(error)) {
            console.log(`getPhoto: giving up while waiting for photo availability (photoId: ${photoId})`, error, {
              photoId,
              size: requestedSize,
            });
            return await this.getFinalFallback(photoId);
          }
          // still missing, or a soft/transient failure — wait for another push
        }
      }
    } finally {
      try {
        subscription.unsubscribe();
      } catch (error) {
        console.log(`getPhoto: realtimeClient unsubscribe failed (photoId: ${photoId})`, error);
      }
    }
  }

  /** Any cached or unsynced original we still have, used as a last resort once every retry path is exhausted. */
  private async getFinalFallback(photoId: string): Promise<string | undefined> {
    const originalPath = await this.fileCache.getFile(getCacheFileName(photoId, ORIGINAL_SIZE));
    if (originalPath) return this.fileSystem.pathToUri(originalPath);
    const unsyncedPath = await this.getUnsyncedOriginalIfPresent(photoId);
    return unsyncedPath ? this.fileSystem.pathToUri(unsyncedPath) : undefined;
  }

  /**
   * Download or scale a photo to the staging directory, then hand it off to the
   * FileCache via addFile. Retries with backoff on transient failures; on a 404
   * (missing on the server), waits for a realtime push telling us it's now available
   * (see waitForPhotoAvailable) instead of giving up immediately.
   */
  private async _getPhoto(photoId: string, requestedSize: PhotoSize): Promise<string | undefined> {
    await this.ensureStagingDirExists();
    const fileName = getCacheFileName(photoId, requestedSize);
    const stagingPath = `${this.stagingDir}${fileName}`;

    let lastError: Error | null = null;
    for (let attempt = 0; attempt < DOWNLOAD_RETRY_LIMIT; attempt++) {
      try {
        return await this.attemptDownloadOrScale(photoId, requestedSize, stagingPath);
      } catch (error) {
        // Best-effort cleanup of a partial staging write before the next attempt.
        await this.cleanupStagingFile(stagingPath);
        if (errorIsMissing(error)) return await this.waitForPhotoAvailable(photoId, requestedSize, stagingPath);
        console.log(`getPhoto (try ${attempt + 1}/${DOWNLOAD_RETRY_LIMIT}): failed to fetch photo`, error, {
          photoId,
          size: requestedSize,
        });
        lastError = error instanceof Error ? error : new Error(String(error));
      }
      await timeout(DOWNLOAD_RETRY_BASE_DELAY_MS * (attempt + 1));
    }

    console[isHardFailure(lastError) ? 'error' : 'log'](`getPhoto: failed to get photo (photoId: ${photoId}, size: ${requestedSize})`, lastError, {
      photoId,
      size: requestedSize,
    });

    return await this.getFinalFallback(photoId);
  }

  private async cleanupStagingFile(path: string): Promise<void> {
    try {
      if (await this.fileSystem.getFileExists(path)) await this.fileSystem.deleteAsync(path);
    } catch {
      // Swallow — orphans in staging are reaped on next gallery init.
    }
  }

  /**
   * Find the smallest cached size that is ≥ requestedSize, falling back to the unsynced
   * original. Used as a scale source when filling a smaller scaled cache entry.
   */
  private async findSmallestCachedAtLeast(photoId: string, requestedSize: PhotoSize): Promise<string | null> {
    const sequence = getPhotoSequenceByDirection(requestedSize, 'higherResolution').filter(size => size !== requestedSize);
    const cached = await this.firstCachedInSequence(photoId, sequence);
    if (cached) return cached.path;
    return (await this.getUnsyncedOriginalIfPresent(photoId)) ?? null;
  }

  /**
   * Walk a size sequence and return the first one that's currently in the FileCache
   * (freshening LRU on the hit). Bulk presence query + per-hit getFile keeps races minimal.
   */
  private async firstCachedInSequence(photoId: string, sequence: PhotoSize[]): Promise<{ path: string; size: PhotoSize } | null> {
    const cacheNames = sequence.map(size => getCacheFileName(photoId, size));
    const infos = await this.fileCache.getFileInfos(cacheNames);
    for (let i = 0; i < sequence.length; i++) {
      if (!infos.has(cacheNames[i])) continue;
      const path = await this.fileCache.getFile(cacheNames[i]);
      if (path) return { path, size: sequence[i] };
      // Raced with an eviction between getFileInfos and getFile — fall through.
    }
    return null;
  }

  /** Doc-dir unsynced original path, only if it currently exists on disk. */
  private async getUnsyncedOriginalIfPresent(photoId: string): Promise<string | undefined> {
    const path = await this.getUnsyncedPhotoPath(photoId);
    return path && await this.fileSystem.getFileExists(path) ? path : undefined;
  }
}
